import { useEffect, useRef, useState, type FC } from 'react';
import '../css/roulette.css';
import { useDocumentTitle } from '../ts/useDocumentTitle';

type SymbolCategory = 'dare' | 'gift' | 'boost';
type OutcomeType = SymbolCategory | 'rare' | 'hydrate';

interface SlotSymbol {
  id: string;
  label: string;
  type: SymbolCategory;
  color: string;
}

const symbols: SlotSymbol[] = [
  { id: 'dare', label: 'Dare', type: 'dare', color: '#FF6B6B' },
  { id: 'gift', label: 'Gift', type: 'gift', color: '#4ECDC4' },
  { id: 'hydrate', label: 'Hydrate', type: 'boost', color: '#85C1E2' },
  { id: 'glow', label: 'Glow', type: 'boost', color: '#F7DC6F' },
  { id: 'energy', label: 'Energy', type: 'gift', color: '#BB8FCE' },
];

const symbolMap = new Map(symbols.map((symbol) => [symbol.id, symbol] as const));

const dareList: string[] = [
  'Take a selfie with someone wearing the wildest outfit you can find.',
  'Start a 10-second dance battle with a stranger.',
  "Compliment five people's outfits in a row.",
  'Get a group of strangers to shout your made-up festival slogan.',
  "Dance like you're in a 2000s music video for one full drop.",
  'Only shuffle for the next three songs.',
  "Copy a stranger's dance moves for 60 seconds.",
  'Hype up the quietest person near you until they start moving.',
  'Make up a fake rave superhero name and introduce yourself to three people with it.',
  'Build a tiny "rave handshake" and use it all night.',
  'Start a small synchronized jump moment when the beat drops.',
  "Pretend you're directing a music video during one full song.",
  'Give away something to someone who looks like they need a boost.',
  'Give a compliment to a stranger.',
  'Fully commit to the nastiest bass face during the heaviest drop.',
  'Dance intensely without smiling for one track. Full warehouse energy.',
  'Only use one repeated movement for two minutes.',
  "Compliment someone's energy, not their looks.",
  'Meow at a stranger.',
  'Take a picture with a real plant.',
  'Start dancing the Macarena. Complete the challenge if you get 2 others to join in.',
  'Get a hug from 2 strangers!',
  'Give 5 people high fives!',
  'Go all out with the silliest dance moves you can think of.',
  'Give a genuine compliment to 5 different people.',
];

const hydrationList: string[] = [
  'Drink water.',
];

const giftList: string[] = [
  'Swap gifts with someone nearby.',
  'Share your favorite track with the group.',
  'Offer to grab water for a friend or stranger.',
  'Give a heartfelt compliment about the vibe.',
  'Teach someone a new dance move.',
  'Fan someone to cool them off.',
];

const boostList: string[] = [
  'Refill your water bottle and take a breather.',
  'Stretch it out and check your posture.',
  'Snap a group photo to capture the energy.',
  'Check in with your crew and share a smile.',
  'Take a deep breath and enjoy the lights.',
  'Get fanned by someone nearby.',
];

const rareRewardList: string[] = [
  'You get a card from the guy with the QR code shirt.',
  'You get gum from the guy with the QR code shirt.',
];

const rareRewardChance = 0.05;
const hydrateChance = 0.1;

const reelCount = 3;
const spinIntervalsMs = 100;
const baseStopDelayMs = 800;
const stopDelayStepMs = 300;
const settleDelayMs = baseStopDelayMs + (reelCount - 1) * stopDelayStepMs + 400;

const createDefaultReels = () => (
  Array.from({ length: reelCount }, (_, index) => symbols[index % symbols.length]?.id ?? symbols[0].id)
);

const getRandomSymbolId = () => {
  const index = Math.floor(Math.random() * symbols.length);
  return symbols[index]?.id ?? symbols[0].id;
};

const getRandomEntry = (entries: string[]) => {
  const index = Math.floor(Math.random() * entries.length);
  return entries[index] ?? '';
};

const getSymbolIdForOutcome = (type: OutcomeType) => {
  switch (type) {
    case 'dare':
      return 'dare';
    case 'gift':
      return 'gift';
    case 'hydrate':
      return 'hydrate';
    case 'boost':
      return 'glow';
    case 'rare':
      return 'energy';
    default:
      return symbols[0].id;
  }
};

const Roulette: FC = () => {
  useDocumentTitle('OHM slot machine');
  const [isSpinning, setIsSpinning] = useState(false);
  const [reels, setReels] = useState<string[]>(createDefaultReels);
  const [message, setMessage] = useState<string | null>(null);
  const [isRareReward, setIsRareReward] = useState(false);
  const intervalsRef = useRef<NodeJS.Timeout[]>([]);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const reelsRef = useRef<string[]>(createDefaultReels());

  useEffect(() => {
    reelsRef.current = reels;
  }, [reels]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const clearTimers = () => {
    intervalsRef.current.forEach((intervalId) => clearInterval(intervalId));
    timeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    intervalsRef.current = [];
    timeoutsRef.current = [];
  };

  const resolveOutcome = (): { message: string; type: OutcomeType } => {
    const finalSymbols = reelsRef.current
      .map((symbolId) => symbolMap.get(symbolId))
      .filter((symbol): symbol is SlotSymbol => Boolean(symbol));

    const hasDare = finalSymbols.some((symbol) => symbol.type === 'dare');
    const hasGift = finalSymbols.some((symbol) => symbol.type === 'gift');

    if (hasDare) {
      const hydrateTriggered = Math.random() < hydrateChance;
      if (hydrateTriggered) {
        return { message: getRandomEntry(hydrationList), type: 'hydrate' };
      }
      return { message: getRandomEntry(dareList), type: 'dare' };
    }
    if (hasGift) {
      return { message: getRandomEntry(giftList), type: 'gift' };
    }
    return { message: getRandomEntry(boostList), type: 'boost' };
  };

  const spinMachine = () => {
    if (isSpinning) return;

    clearTimers();
    setIsSpinning(true);
    setMessage(null);
    setIsRareReward(false);

    for (let index = 0; index < reelCount; index += 1) {
      const intervalId = setInterval(() => {
        setReels((previous) => {
          const next = [...previous];
          next[index] = getRandomSymbolId();
          return next;
        });
      }, spinIntervalsMs);
      intervalsRef.current.push(intervalId);

      const stopTimeout = setTimeout(() => {
        clearInterval(intervalId);
        intervalsRef.current = intervalsRef.current.filter((stored) => stored !== intervalId);
        setReels((previous) => {
          const next = [...previous];
          next[index] = getRandomSymbolId();
          return next;
        });
      }, baseStopDelayMs + index * stopDelayStepMs);
      timeoutsRef.current.push(stopTimeout);
    }

    const settleTimeout = setTimeout(() => {
      setIsSpinning(false);
      const rewardTriggered = Math.random() < rareRewardChance;
      if (rewardTriggered) {
        const messageText = getRandomEntry(rareRewardList);
        const finalSymbols = Array(reelCount).fill(getSymbolIdForOutcome('rare'));
        setMessage(messageText);
        setIsRareReward(true);
        setReels(finalSymbols);
        reelsRef.current = finalSymbols;
      } else {
        const { message: resolvedMessage, type } = resolveOutcome();
        const finalSymbols = Array(reelCount).fill(getSymbolIdForOutcome(type));
        setMessage(resolvedMessage);
        setIsRareReward(false);
        setReels(finalSymbols);
        reelsRef.current = finalSymbols;
      }
    }, settleDelayMs);
    timeoutsRef.current.push(settleTimeout);
  };

  return (
    <div className="slot-machine-container">
      <h1>OHM slot machine</h1>

      <div className="slot-machine-body">
        <div className="slot-top-light"></div>
        <div className="slot-window">
          {reels.map((symbolId, index) => {
            const symbol = symbolMap.get(symbolId) ?? symbols[0];
            return (
              <div
                key={`${symbol.id}-${index}`}
                className={`reel ${isSpinning ? 'reel-spinning' : ''}`}
              >
                <div
                  className="reel-face"
                  style={{ backgroundColor: symbol.color }}
                >
                  <span>{symbol.label}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="slot-bottom-light"></div>

        {message && (
          <div
            className="slot-result-overlay"
            role="button"
            tabIndex={0}
            onClick={() => {
              setMessage(null);
              setIsRareReward(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setMessage(null);
                setIsRareReward(false);
              }
            }}
            aria-label="Dismiss challenge"
          >
            <div className={`slot-result ${isRareReward ? 'slot-result-celebrate' : ''}`}>
              <h2>Result</h2>
              <p>{message}</p>
              <p className="slot-result-hint">Tap to close</p>
            </div>
          </div>
        )}
      </div>

      <div className="controls">
        <button
          onClick={spinMachine}
          disabled={isSpinning}
          className="spin-button"
        >
          {isSpinning ? 'Spinning...' : 'Pull the Lever'}
        </button>
      </div>

    </div>
  );
};

export default Roulette;
